import "@/App.css";
import {
  Shield,
  QrCode,
  Car,
  Users,
  FileText,
  ScrollText,
  Download,
  CheckCircle2,
  Sparkles,
  WalletCards,
  BadgeCheck,
  Building2,
  GraduationCap,
  Briefcase,
  Stethoscope,
  BedDouble,
  Factory,
  Warehouse,
  Anchor,
  PartyPopper,
  Mail,
  Globe,
  ArrowRight,
  ShieldCheck,
  Heart,
  Lock,
  HandHeart,
  UserCog,
  UserCheck,
  UsersRound,
  KeyRound,
  Menu,
  X,
  Clock,
  Cloud,
  Zap,
  TrendingUp,
} from "lucide-react";
import { useState, createContext, useContext, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import EmailActionDialog from "@/components/EmailActionDialog";

const SUPPORT_EMAIL = "unigateappinfo@gmail.com";

// Context to trigger email dialog from any CTA
const EmailDialogContext = createContext({ openDemo: () => {}, openContact: () => {} });
const useEmailDialog = () => useContext(EmailDialogContext);

// Scrolls to top of the page on every route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ugatepass-landing/artifacts/vg9ku1n9_Logo%20for%20Web.png";

// --- NAVBAR ---
const NavBar = () => {
  const [open, setOpen] = useState(false);
  const { openDemo } = useEmailDialog();
  const links = [
    { href: "#why", label: "Why Us" },
    { href: "#features", label: "Features" },
    { href: "#vehicle", label: "Vehicle Passes" },
    { href: "#how", label: "How It Works" },
    { href: "#industries", label: "Industries" },
    { href: "#contact", label: "Contact" },
  ];
  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-3">
        <a href="#top" className="flex items-center gap-2" data-testid="nav-logo">
          <img
            src={LOGO_URL}
            alt="UGatePass"
            className="h-9 w-9 sm:h-10 sm:w-10 lg:h-11 lg:w-11 object-contain mix-blend-multiply"
          />
          <span className="font-extrabold text-[#23395D] text-lg sm:text-xl tracking-tight leading-none">
            UGate<span className="text-[#4CAF50]">Pass</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-[15px] text-slate-700 hover:text-[#1F5FBF] transition-colors font-medium"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button
            type="button"
            onClick={openDemo}
            data-testid="nav-request-demo"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white font-semibold text-sm bg-gradient-to-r from-[#1F5FBF] to-[#4CAF50] hover:opacity-90 shadow-sm transition-all"
          >
            Request Demo <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="md:hidden p-2 text-[#23395D]"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white" data-testid="nav-mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-slate-700 py-2 font-medium"
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                openDemo();
              }}
              data-testid="nav-mobile-request-demo"
              className="mt-2 inline-flex justify-center items-center gap-1.5 px-4 py-2.5 rounded-full text-white font-semibold text-sm bg-gradient-to-r from-[#1F5FBF] to-[#4CAF50]"
            >
              Request Demo <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

const BANNER_URL =
  "https://customer-assets.emergentagent.com/job_ugatepass-landing/artifacts/q7ww0448_Banner%20web%203.png";

const HERO_CAR_IMG =
  "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=900&q=80&auto=format&fit=crop";

// --- HERO ---
const Hero = () => {
  const pillars = [
    {
      icon: Lock,
      title: "Enhanced Security",
      desc: "Digitized gate pass system ensures secure and controlled access.",
      color: "text-[#1F5FBF]",
    },
    {
      icon: Clock,
      title: "Real-Time Tracking",
      desc: "Monitor entry & exit in real-time with detailed logs and reports.",
      color: "text-[#1F5FBF]",
    },
    {
      icon: QrCode,
      title: "QR Code Verification",
      desc: "Fast and contactless verification for seamless access.",
      color: "text-[#1F5FBF]",
    },
    {
      icon: Users,
      title: "Role-Based Access",
      desc: "Granular access control for Admins, Gate Staff, Members and Visitors.",
      color: "text-[#4CAF50]",
    },
    {
      icon: Cloud,
      title: "Cloud Based",
      desc: "Access your data anytime, anywhere with secure cloud storage.",
      color: "text-[#1F5FBF]",
    },
    {
      icon: FileText,
      title: "Reports & Analytics",
      desc: "Powerful insights to help you make smarter decisions.",
      color: "text-[#4CAF50]",
    },
  ];

  const indicators = [
    { icon: ShieldCheck, label: "Secure", sub: "Every Entry", color: "from-[#1F5FBF] to-[#3A7BD5]" },
    { icon: Zap, label: "Smart", sub: "Every Step", color: "from-[#3A7BD5] to-[#4CAF50]" },
    { icon: TrendingUp, label: "Insightful", sub: "Every Time", color: "from-[#1F5FBF] to-[#4CAF50]" },
    { icon: CheckCircle2, label: "Simple", sub: "Always", color: "from-[#4CAF50] to-[#8BC34A]" },
  ];

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="bg-white border-b border-slate-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 pt-5 sm:pt-8 lg:pt-10 pb-12 lg:pb-16">
        {/* Top row: brand block + 6 feature pillars */}
        <div className="grid lg:grid-cols-[minmax(0,42%)_1fr] gap-8 lg:gap-10 items-start">
          {/* Brand block — dominant identity lockup
              Mobile: stacked & centered (logo above name)
              Desktop: side-by-side */}
          <div
            data-testid="hero-brand-block"
            className="flex flex-col items-center text-center lg:flex-row lg:items-start lg:text-left gap-4 sm:gap-5 lg:gap-6 w-full lg:w-auto"
          >
            <img
              src={LOGO_URL}
              alt="UGatePass"
              className="h-32 w-32 sm:h-36 sm:w-36 lg:h-48 lg:w-48 object-contain mix-blend-multiply flex-shrink-0 scale-110"
            />
            <div className="leading-tight lg:pt-2 w-full lg:w-auto min-w-0">
              <div className="font-extrabold text-[#23395D] text-[2.6rem] sm:text-5xl lg:text-[3.2rem] tracking-tight leading-[0.95] whitespace-nowrap">
                UGate<span className="text-[#4CAF50]">Pass</span>
              </div>
              <div className="text-base sm:text-lg lg:text-[1.3rem] text-slate-500 mt-2.5 sm:mt-3 lg:mt-4 font-medium">
                Your Universal Gateway
              </div>
              <div className="h-[3px] w-36 sm:w-44 lg:w-52 bg-gradient-to-r from-[#1F5FBF] to-[#4CAF50] mt-3 lg:mt-4 mx-auto lg:mx-0" />
              <div className="text-sm sm:text-base lg:text-[1.05rem] text-[#1F5FBF] font-semibold mt-3 lg:mt-4">
                One Pass. Infinite Possibilities.
              </div>
            </div>
          </div>

          {/* 6 pillars */}
          <div
            data-testid="hero-pillars"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-5 lg:gap-3"
          >
            {pillars.map((p, i) => (
              <div
                key={p.title}
                data-testid={`hero-pillar-${i}`}
                className="flex flex-col items-start"
              >
                <div
                  className={`w-10 h-10 lg:w-11 lg:h-11 rounded-lg border-2 border-current ${p.color} flex items-center justify-center mb-2`}
                >
                  <p.icon className="w-5 h-5 lg:w-6 lg:h-6" strokeWidth={2} />
                </div>
                <div
                  className={`text-[11px] lg:text-xs font-extrabold uppercase tracking-[0.05em] ${p.color} leading-tight`}
                >
                  {p.title}
                </div>
                <p className="mt-1.5 text-[11px] lg:text-[11.5px] text-slate-600 leading-snug">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Main content: headline + indicators (left) / car + phone (right) */}
        <div className="mt-10 lg:mt-14 grid lg:grid-cols-[1fr_1.05fr] gap-10 lg:gap-12 items-center">
          {/* Left */}
          <div>
            <h1
              data-testid="hero-headline"
              className="text-3xl sm:text-4xl lg:text-[3.2rem] font-extrabold leading-[1.08] tracking-tight"
            >
              <span className="text-[#23395D] block">Smart Gate Pass</span>
              <span className="text-[#23395D] block">Management</span>
              <span className="text-[#4CAF50] block">for a Safer Tomorrow</span>
            </h1>
            <p
              data-testid="hero-subheadline"
              className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl"
            >
              UGatePass is a comprehensive digital gate pass solution that
              simplifies entry &amp; exit management for any facility.
            </p>

            <div
              data-testid="hero-indicators"
              className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4"
            >
              {indicators.map((ind) => (
                <div
                  key={ind.label}
                  className="flex items-center gap-2.5"
                >
                  <div
                    className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br ${ind.color} flex items-center justify-center text-white shadow-sm flex-shrink-0`}
                  >
                    <ind.icon className="w-5 h-5" />
                  </div>
                  <div className="leading-tight">
                    <div className="font-extrabold text-[#23395D] text-sm">
                      {ind.label}
                    </div>
                    <div className="text-[11px] text-slate-500">{ind.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right visual */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-[0_20px_50px_-15px_rgba(35,57,93,0.35)] aspect-[5/4] sm:aspect-[4/3] bg-slate-100">
              <img
                src={HERO_CAR_IMG}
                alt="Luxury vehicle at facility gate"
                loading="eager"
                fetchPriority="high"
                data-testid="hero-car-image"
                className="w-full h-full object-cover"
              />
              {/* Skyline gradient overlay for cohesion */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#23395D]/35 via-transparent to-transparent pointer-events-none" />
            </div>

            {/* VEHICLE PASS floating badge */}
            <div
              data-testid="hero-vehicle-pass-badge"
              className="absolute bottom-3 left-3 sm:bottom-5 sm:left-5 bg-gradient-to-r from-[#4CAF50] to-[#3A7BD5] text-white rounded-2xl px-3.5 py-2.5 shadow-xl flex items-center gap-2.5 max-w-[220px] sm:max-w-[260px]"
            >
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0 border border-white/25">
                <Car className="w-5 h-5" />
              </div>
              <div>
                <div className="font-extrabold text-xs sm:text-sm leading-tight tracking-wide">
                  VEHICLE PASS
                </div>
                <div className="text-[10px] sm:text-[11px] text-white/90 leading-snug mt-0.5">
                  Manage vehicle movement with dedicated vehicle passes.
                </div>
              </div>
            </div>

            {/* Phone mockup */}
            <div
              data-testid="hero-phone-mock"
              className="absolute -top-4 right-2 sm:-top-6 sm:right-4 lg:-top-2 lg:-right-2 w-32 sm:w-40 lg:w-48 bg-[#23395D] rounded-[1.5rem] p-1.5 shadow-2xl ring-4 ring-white"
            >
              <div className="bg-white rounded-[1.1rem] p-2.5 sm:p-3">
                <div className="flex items-center gap-1.5">
                  <img
                    src={LOGO_URL}
                    alt=""
                    className="w-5 h-5 sm:w-6 sm:h-6 object-contain mix-blend-multiply"
                  />
                  <div className="font-extrabold text-[#23395D] text-[10px] sm:text-xs leading-none">
                    UGate<span className="text-[#4CAF50]">Pass</span>
                  </div>
                </div>
                <div className="mt-2 text-[8px] sm:text-[9px] font-extrabold px-1.5 py-0.5 inline-block bg-[#1F5FBF]/10 text-[#1F5FBF] rounded tracking-wider">
                  VISITOR PASS
                </div>
                <div className="mt-1.5 text-[10px] sm:text-xs font-extrabold text-[#23395D] leading-tight">
                  John Doe
                </div>
                <div className="text-[8px] sm:text-[9px] text-slate-500">
                  Meeting
                </div>
                <div className="mt-1 flex items-center justify-between text-[7.5px] sm:text-[8.5px] text-slate-500 leading-tight">
                  <span>26-06-2026</span>
                  <span>10:30 AM</span>
                </div>
                <div className="text-[7.5px] sm:text-[8.5px] text-slate-500 leading-tight flex items-center justify-between">
                  <span>Gate No. 2</span>
                  <span>Entry</span>
                </div>
                <div className="mt-1.5 aspect-square bg-white border border-slate-200 rounded-md flex items-center justify-center">
                  <QrCode
                    className="w-10 h-10 sm:w-12 sm:h-12 text-[#23395D]"
                    strokeWidth={1.4}
                  />
                </div>
                <div className="mt-1 text-[7px] sm:text-[8px] text-center text-slate-500 font-medium">
                  Pass ID: UG126789
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- IDEAL FOR EVERY FACILITY ---
const FACILITY_IMAGES = {
  apartments:
    "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400&q=75&auto=format&fit=crop",
  schools:
    "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=400&q=75&auto=format&fit=crop",
  offices:
    "https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&q=75&auto=format&fit=crop",
  hospitals:
    "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400&q=75&auto=format&fit=crop",
  warehouses:
    "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=400&q=75&auto=format&fit=crop",
  events:
    "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&q=75&auto=format&fit=crop",
  ports:
    "https://images.unsplash.com/photo-1577415124269-fc1140a69e91?w=400&q=75&auto=format&fit=crop",
  hostels:
    "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=400&q=75&auto=format&fit=crop",
};

const IdealForFacility = () => {
  const facilities = [
    { name: "Apartments & Communities", icon: Building2, img: FACILITY_IMAGES.apartments, color: "bg-[#1F5FBF]" },
    { name: "Schools & Colleges", icon: GraduationCap, img: FACILITY_IMAGES.schools, color: "bg-[#4CAF50]" },
    { name: "Offices & Corporates", icon: Briefcase, img: FACILITY_IMAGES.offices, color: "bg-[#1F5FBF]" },
    { name: "Hospitals & Clinics", icon: Stethoscope, img: FACILITY_IMAGES.hospitals, color: "bg-[#1F5FBF]" },
    { name: "Warehouses & Factories", icon: Warehouse, img: FACILITY_IMAGES.warehouses, color: "bg-[#4CAF50]" },
    { name: "Events & Exhibitions", icon: PartyPopper, img: FACILITY_IMAGES.events, color: "bg-[#1F5FBF]" },
    { name: "Ports & Terminals", icon: Anchor, img: FACILITY_IMAGES.ports, color: "bg-[#1F5FBF]" },
    { name: "Hostels & PGs", icon: BedDouble, img: FACILITY_IMAGES.hostels, color: "bg-[#1F5FBF]" },
  ];

  return (
    <section
      id="ideal-facility"
      data-testid="ideal-facility-section"
      className="bg-white py-12 lg:py-16 border-b border-slate-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8">
        <div className="flex items-center justify-center gap-3 lg:gap-6">
          <div className="h-px flex-1 bg-slate-200 max-w-[160px] sm:max-w-[220px]" />
          <h2
            data-testid="ideal-facility-title"
            className="text-2xl sm:text-3xl lg:text-[2rem] font-extrabold text-[#23395D] tracking-tight text-center px-1"
          >
            Ideal for Every Facility
          </h2>
          <div className="h-px flex-1 bg-slate-200 max-w-[160px] sm:max-w-[220px]" />
        </div>

        <div className="mt-8 lg:mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-9 gap-3 lg:gap-3">
          {facilities.map((f, i) => (
            <a
              href="#industries"
              key={f.name}
              data-testid={`ideal-facility-${i}`}
              className="group rounded-xl overflow-hidden shadow-sm border border-slate-200 hover:shadow-lg hover:-translate-y-0.5 transition-all bg-white"
            >
              <div className="aspect-square bg-slate-100 overflow-hidden">
                <img
                  src={f.img}
                  alt={f.name}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div
                className={`${f.color} text-white px-2 py-2.5 flex items-center gap-1.5`}
              >
                <f.icon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2.5} />
                <span className="text-[10.5px] lg:text-[11px] font-extrabold leading-tight tracking-tight">
                  {f.name}
                </span>
              </div>
            </a>
          ))}

          {/* "And many more" tile */}
          <div
            data-testid="ideal-facility-more"
            className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center text-center p-3 aspect-[1/1.18]"
          >
            <Building2 className="w-7 h-7 lg:w-8 lg:h-8 text-slate-400 mb-1.5" strokeWidth={1.5} />
            <div className="text-[10.5px] lg:text-[11px] font-semibold text-slate-600 leading-tight">
              And many more types of facilities...
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- SECTION HEADING ---
const SectionHeading = ({ eyebrow, title, subtitle, testid }) => (
  <div className="text-center max-w-3xl mx-auto" data-testid={`${testid}-heading`}>
    {eyebrow && (
      <div className="inline-block text-xs font-bold uppercase tracking-[0.18em] text-[#1F5FBF] mb-3">
        {eyebrow}
      </div>
    )}
    <h2 className="text-3xl sm:text-4xl font-extrabold text-[#23395D] leading-tight tracking-tight">
      {title}
    </h2>
    {subtitle && (
      <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
        {subtitle}
      </p>
    )}
  </div>
);

// --- WHY UGATEPASS ---
const Why = () => {
  const items = [
    { icon: Sparkles, title: "Easy to Use", desc: "Intuitive interface designed for staff and admins." },
    { icon: WalletCards, title: "Affordable", desc: "Built for organizations of every size." },
    { icon: BadgeCheck, title: "Ad-Free Experience", desc: "No ads. No distractions. Just work." },
    { icon: ShieldCheck, title: "Secure & Reliable", desc: "Trusted access management you can count on." },
    { icon: QrCode, title: "QR-Based Validation", desc: "Verify visitors and vehicles in seconds." },
    { icon: Building2, title: "Multi-Facility Support", desc: "Manage multiple gates and locations." },
    { icon: ScrollText, title: "Digital Records & Logs", desc: "Replace registers with searchable digital logs." },
    { icon: Car, title: "Visitor + Vehicle in One", desc: "One unified platform for every kind of pass." },
  ];
  return (
    <section id="why" data-testid="why-section" className="bg-white py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeading
          eyebrow="Why UGatePass"
          title="Built for secure, modern access management"
          subtitle="UGatePass meets the growing need for secure, affordable, and efficient access management across residential, educational, commercial, and industrial facilities."
          testid="why"
        />
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((it, i) => (
            <div
              key={it.title}
              data-testid={`why-card-${i}`}
              className="group p-6 rounded-2xl bg-white border border-slate-200 hover:border-[#1F5FBF]/40 hover:shadow-[0_8px_30px_rgb(31,95,191,0.08)] transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#1F5FBF] to-[#4CAF50] flex items-center justify-center text-white shadow-sm">
                <it.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-4 font-bold text-[#23395D] text-base">{it.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- KEY FEATURES ---
const Features = () => {
  const features = [
    { icon: Users, title: "Visitor Management", desc: "Create and manage visitor passes digitally with unique UGatePass IDs and QR codes." },
    { icon: QrCode, title: "QR-Based Validation", desc: "Gate staff validate visitors instantly using QR codes, pass details, photos, or ID information." },
    { icon: Car, title: "Vehicle Pass Management", desc: "Manage vehicle passes with details, driver info, schedules, and complete movement logs." },
    { icon: KeyRound, title: "Role-Based Access Control", desc: "Separate permissions for Super Admin, Admin, Gate Staff, Members, and authorized users." },
    { icon: FileText, title: "Digital Entry & Exit Records", desc: "Maintain complete digital records of all visitor and vehicle movements." },
    { icon: ScrollText, title: "Vehicle Logs", desc: "Track and search vehicle movements with date-based records and full history." },
    { icon: Download, title: "PDF Export & Sharing", desc: "Generate and share digital passes and reports easily across teams." },
  ];
  return (
    <section
      id="features"
      data-testid="features-section"
      className="bg-[#F5F8FC] py-20 lg:py-24"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeading
          eyebrow="Key Features"
          title="Everything you need at the gate"
          subtitle="A complete toolkit to digitize gate operations — from pass creation to real-time validation and reporting."
          testid="features"
        />
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div
              key={f.title}
              data-testid={`feature-card-${i}`}
              className="p-7 bg-white rounded-2xl border border-slate-200 hover:shadow-[0_10px_40px_rgb(31,95,191,0.08)] hover:-translate-y-1 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-[#1F5FBF]/10 flex items-center justify-center text-[#1F5FBF]">
                <f.icon className="w-6 h-6" />
              </div>
              <h3 className="mt-5 font-bold text-[#23395D] text-lg">{f.title}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed text-[15px]">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- VEHICLE PASS MANAGEMENT ---
const VehiclePass = () => {
  const fields = [
    "Vehicle Number",
    "Driver Details",
    "Purpose of Visit",
    "Entry Schedule",
    "Exit Schedule",
    "QR Code Validation",
    "Vehicle Movement Records",
  ];
  const idealFor = [
    "Ports",
    "Warehouses",
    "Logistics Centers",
    "Factories",
    "Industrial Parks",
    "Hospitals",
    "Educational Institutions",
    "Residential Communities",
    "Events",
  ];
  const benefits = [
    "Faster vehicle verification",
    "Reduced manual registers",
    "Improved security",
    "Digital movement records",
    "Vehicle search & history",
    "Better compliance & audit readiness",
  ];
  return (
    <section id="vehicle" data-testid="vehicle-section" className="bg-white py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <div className="inline-block text-xs font-bold uppercase tracking-[0.18em] text-[#4CAF50] mb-3">
            Vehicle Pass Management
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#23395D] leading-tight tracking-tight">
            Not just visitors. Vehicles too.
          </h2>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed">
            UGatePass is also a complete Vehicle Entry &amp; Exit Management
            System. Organizations can issue vehicle-specific digital passes,
            track movements, and maintain audit-ready logs — all in one place.
          </p>

          <div className="mt-6">
            <div className="text-sm font-semibold text-[#23395D] mb-2">Ideal for</div>
            <div className="flex flex-wrap gap-2" data-testid="vehicle-ideal-for">
              {idealFor.map((x) => (
                <span
                  key={x}
                  className="text-sm px-3 py-1.5 rounded-full bg-[#1F5FBF]/8 text-[#1F5FBF] border border-[#1F5FBF]/15 font-medium"
                >
                  {x}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <div className="text-sm font-semibold text-[#23395D] mb-3">Benefits</div>
            <div className="grid sm:grid-cols-2 gap-2.5" data-testid="vehicle-benefits">
              {benefits.map((b) => (
                <div key={b} className="flex items-start gap-2 text-slate-700 text-[15px]">
                  <CheckCircle2 className="w-5 h-5 text-[#4CAF50] mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Vehicle pass card */}
        <div className="bg-gradient-to-br from-[#F5F8FC] to-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-200">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1F5FBF] to-[#4CAF50] flex items-center justify-center text-white">
              <Car className="w-5 h-5" />
            </div>
            <div>
              <div className="font-bold text-[#23395D]">Vehicle Pass Contains</div>
              <div className="text-xs text-slate-500">All fields captured digitally</div>
            </div>
          </div>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            {fields.map((field) => (
              <div
                key={field}
                className="flex items-center gap-2.5 p-3 rounded-xl bg-white border border-slate-200"
              >
                <CheckCircle2 className="w-4 h-4 text-[#4CAF50] flex-shrink-0" />
                <span className="text-sm font-medium text-[#23395D]">{field}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// --- HOW IT WORKS ---
const HowItWorks = () => {
  const steps = [
    { n: "01", title: "Facility Setup", desc: "Super Admins and Admins create facilities and configure gates, users, and pass purposes." },
    { n: "02", title: "Pass Creation", desc: "Authorized users create UGatePasses for self, visitors, guests, vehicles, contractors, deliveries, interviews, meetings and events." },
    { n: "03", title: "QR Generation", desc: "Each pass is issued with a unique UGatePass ID and QR Code." },
    { n: "04", title: "Validation", desc: "Gate Staff validate visitors or vehicles using QR codes and supporting details." },
    { n: "05", title: "Entry & Exit Recording", desc: "Gate Staff record entry and exit activities digitally with comments and verification details." },
    { n: "06", title: "Digital Logs", desc: "All records are stored securely for future reference, search, and reporting." },
  ];
  return (
    <section id="how" data-testid="how-section" className="bg-[#F5F8FC] py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeading
          eyebrow="How UGatePass Works"
          title="From setup to digital logs — in six steps"
          subtitle="A guided journey that takes your gate from paper registers to a fully digital, audit-ready workflow."
          testid="how"
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div
              key={s.n}
              data-testid={`how-step-${i}`}
              className="relative p-7 bg-white rounded-2xl border border-slate-200 hover:border-[#4CAF50]/50 hover:shadow-[0_10px_40px_rgb(76,175,80,0.10)] transition-all"
            >
              <div className="text-5xl font-extrabold bg-gradient-to-r from-[#1F5FBF] to-[#4CAF50] bg-clip-text text-transparent leading-none">
                {s.n}
              </div>
              <h3 className="mt-3 font-bold text-[#23395D] text-lg">{s.title}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed text-[15px]">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- ROLES & PERMISSIONS ---
const Roles = () => {
  const roles = [
    {
      icon: ShieldCheck,
      title: "Super Admin",
      desc: "Owns the full platform. Creates facilities, configures gates, manages Admins and global settings.",
      color: "from-[#1F5FBF] to-[#3A7BD5]",
    },
    {
      icon: UserCog,
      title: "Admin",
      desc: "Manages a specific facility — adds members, configures pass purposes, and oversees operations.",
      color: "from-[#3A7BD5] to-[#4CAF50]",
    },
    {
      icon: UserCheck,
      title: "Gate Staff",
      desc: "Validates QR-based passes, records entry and exit, and verifies visitor and vehicle details.",
      color: "from-[#4CAF50] to-[#8BC34A]",
    },
    {
      icon: UsersRound,
      title: "Members",
      desc: "Residents, employees, students, or staff registered to the facility. Can create passes when permitted.",
      color: "from-[#1F5FBF] to-[#4CAF50]",
    },
    {
      icon: KeyRound,
      title: "Authorized Users",
      desc: "Designated personnel (HR, Reception, Wardens, Supervisors) empowered to create and approve passes.",
      color: "from-[#3A7BD5] to-[#8BC34A]",
    },
  ];
  return (
    <section id="roles" data-testid="roles-section" className="bg-white py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeading
          eyebrow="Roles & Permissions"
          title="The right access for every person"
          subtitle="Role-based access control ensures every user — from Super Admin to Gate Staff — has exactly what they need, and nothing more."
          testid="roles"
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {roles.map((r, i) => (
            <div
              key={r.title}
              data-testid={`role-card-${i}`}
              className="p-6 rounded-2xl bg-[#F5F8FC] border border-slate-200 hover:shadow-[0_8px_30px_rgb(31,95,191,0.08)] transition-all"
            >
              <div
                className={`w-12 h-12 rounded-xl bg-gradient-to-br ${r.color} flex items-center justify-center text-white shadow-sm`}
              >
                <r.icon className="w-6 h-6" />
              </div>
              <h3 className="mt-4 font-bold text-[#23395D] text-base">{r.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- INDUSTRIES ---
const Industries = () => {
  const items = [
    { icon: Building2, title: "Apartments & Residential Communities", desc: "Residents are registered as Members. All authorized users can create UGatePasses for self, other members and guests — for deliveries, maintenance and visitors. Gate Staff validate and record movements." },
    { icon: GraduationCap, title: "Schools & Educational Institutions", desc: "Students, Parents, Teachers and Staff are registered as Members. All authorized users can create UGatePasses for self, other members and guests — for students, parents, visitors, interviews, vendors and events. Gate Staff manage all access." },
    { icon: Briefcase, title: "Offices & Corporate Facilities", desc: "Employees are registered as Members. All authorized users (HR, Reception, Management and permitted employees) can create UGatePasses for self, other members and guests — visitors, vendors, contractors and interview candidates." },
    { icon: Stethoscope, title: "Hospitals", desc: "Hospital staff are registered as Members. All authorized users can create UGatePasses for self, other members and guests — for visitors, attendants, vendors, contractors and service providers, with QR-based gate validation." },
    { icon: BedDouble, title: "Hostels", desc: "Residents are registered as Members. All authorized users (Wardens and permitted staff) can create UGatePasses for self, other members and guests, and manage visitor permissions with ease." },
    { icon: Factory, title: "Factories & Industrial Facilities", desc: "Employees are registered as Members. All authorized users (Supervisors and Administrators) can create UGatePasses for self, other members and guests — for contractors, vendors, visitors and vehicles. Security maintains digital movement records." },
    { icon: Warehouse, title: "Warehouses & Logistics Centers", desc: "Staff are registered as Members. All authorized users can create UGatePasses for self, other members and guests — for delivery personnel, transport operators and vehicle movements, with complete entry/exit records." },
    { icon: Anchor, title: "Ports & Harbours", desc: "Operations staff are registered as Members. All authorized users can create UGatePasses for self, other members and guests — issuing vehicle passes for trucks, transport, contractors and visitors with secure gate validations." },
    { icon: PartyPopper, title: "Events & Functions", desc: "Organizers and crew are registered as Members. All authorized users can create UGatePasses for self, other members and guests — for participants, vendors and exhibitors, with efficient QR validation." },
  ];
  return (
    <section
      id="industries"
      data-testid="industries-section"
      className="bg-[#F5F8FC] py-20 lg:py-24"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeading
          eyebrow="Industries Served"
          title="Built for every kind of gate"
          subtitle="Across every industry, all authorized users can create UGatePasses for themselves, for other members and for guests — adapting to the way each organization works."
          testid="industries"
        />
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <div
              key={it.title}
              data-testid={`industry-card-${i}`}
              className="p-7 bg-white rounded-2xl border border-slate-200 hover:border-[#4CAF50]/40 hover:shadow-[0_10px_40px_rgb(76,175,80,0.10)] hover:-translate-y-1 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1F5FBF] to-[#4CAF50] flex items-center justify-center text-white">
                <it.icon className="w-6 h-6" />
              </div>
              <h3 className="mt-5 font-bold text-[#23395D] text-lg leading-snug">
                {it.title}
              </h3>
              <p className="mt-2 text-slate-600 leading-relaxed text-[15px]">
                {it.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- COMMITMENT ---
const Commitment = () => {
  const pillars = [
    { icon: WalletCards, title: "Affordable Solution", desc: "Designed to be accessible for organizations of all sizes." },
    { icon: BadgeCheck, title: "Ad-Free Experience", desc: "Operate without ads or unnecessary distractions." },
    { icon: Lock, title: "Data Protection", desc: "Customer information protected through secure system practices." },
    { icon: HandHeart, title: "Social Responsibility", desc: "Improving security, accountability and operational efficiency for communities." },
  ];
  return (
    <section
      id="commitment"
      data-testid="commitment-section"
      className="bg-white py-20 lg:py-24"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeading
          eyebrow="Our Commitment"
          title="Trustworthy by design"
          subtitle="UGatePass is built around a simple promise — affordable, secure, ad-free access management you can rely on."
          testid="commitment"
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {pillars.map((p, i) => (
            <div
              key={p.title}
              data-testid={`commitment-card-${i}`}
              className="p-6 rounded-2xl bg-[#F5F8FC] border border-slate-200 hover:shadow-[0_8px_30px_rgb(31,95,191,0.08)] transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1F5FBF] to-[#4CAF50] flex items-center justify-center text-white shadow-sm">
                <p.icon className="w-6 h-6" />
              </div>
              <h3 className="mt-4 font-bold text-[#23395D] text-base">{p.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- ABOUT ---
const About = () => (
  <section id="about" data-testid="about-section" className="bg-[#F5F8FC] py-20 lg:py-24">
    <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
      <div className="inline-block text-xs font-bold uppercase tracking-[0.18em] text-[#1F5FBF] mb-3">
        About UGatePass
      </div>
      <h2 className="text-3xl sm:text-4xl font-extrabold text-[#23395D] leading-tight tracking-tight">
        Replacing manual registers with modern digital processes
      </h2>
      <p className="mt-6 text-slate-600 text-lg leading-relaxed">
        UGatePass is a digital Entry &amp; Exit Management application designed
        to simplify visitor, member, vehicle and gate access management. The
        platform supports Apartments, Communities, Schools, Offices, Hospitals,
        Hostels, Factories, Warehouses, Ports and Events through a flexible and
        role-based access management system.
      </p>
      <p className="mt-4 text-slate-600 text-lg leading-relaxed">
        Our mission is to provide an affordable, secure and easy-to-use platform
        that helps organizations modernize the way they manage their gates.
      </p>

      <div className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#1F5FBF] to-[#4CAF50] text-white text-sm font-semibold">
        <Sparkles className="w-4 h-4" /> Your Universal Gateway
      </div>
    </div>
  </section>
);

// --- CTA BAND ---
const CTABand = () => {
  const { openDemo, openContact } = useEmailDialog();
  return (
  <section data-testid="cta-band" className="relative overflow-hidden">
    <div className="bg-gradient-to-r from-[#1F5FBF] via-[#3A7BD5] to-[#4CAF50]">
      <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_70%_30%,white_1px,transparent_1px)] [background-size:22px_22px]" />
      <div className="relative max-w-5xl mx-auto px-5 lg:px-8 py-16 lg:py-20 text-center text-white">
        <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight">
          Ready to digitize your gate?
        </h2>
        <p className="mt-3 text-white/90 text-lg max-w-2xl mx-auto">
          Get a personalized walkthrough of UGatePass for your organization — in
          under 30 minutes.
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={openDemo}
            data-testid="cta-band-request-demo"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-[#1F5FBF] font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
          >
            Request Demo <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={openContact}
            data-testid="cta-band-contact"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/10 border border-white/40 text-white font-bold backdrop-blur hover:bg-white/20 transition-all"
          >
            Contact Us
          </button>
        </div>
      </div>
    </div>
  </section>
  );
};

// --- CONTACT ---
const Contact = () => {
  const { openDemo, openContact } = useEmailDialog();
  return (
  <section id="contact" data-testid="contact-section" className="bg-white py-20 lg:py-24">
    <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <div className="inline-block text-xs font-bold uppercase tracking-[0.18em] text-[#1F5FBF] mb-3">
          Contact Us
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-[#23395D] leading-tight tracking-tight">
          Let&apos;s talk about your gate
        </h2>
        <p className="mt-4 text-slate-600 text-lg leading-relaxed">
          Whether you&apos;re a community, school, factory or port — our team is
          happy to help you get started.
        </p>

        <div className="mt-8 space-y-4">
          <a
            href="https://www.ugatepass.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-website"
            className="flex items-center gap-4 p-4 rounded-2xl bg-[#F5F8FC] border border-slate-200 hover:border-[#1F5FBF]/40 transition-all"
          >
            <div className="w-11 h-11 rounded-xl bg-[#1F5FBF]/10 text-[#1F5FBF] flex items-center justify-center">
              <Globe className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Website</div>
              <div className="font-semibold text-[#23395D]">www.ugatepass.com</div>
            </div>
          </a>

          <button
            type="button"
            onClick={openDemo}
            data-testid="contact-demo-email"
            className="w-full text-left flex items-center gap-4 p-4 rounded-2xl bg-[#F5F8FC] border border-slate-200 hover:border-[#4CAF50]/40 transition-all"
          >
            <div className="w-11 h-11 rounded-xl bg-[#4CAF50]/10 text-[#4CAF50] flex items-center justify-center">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Request a Demo</div>
              <div className="font-semibold text-[#23395D]">{SUPPORT_EMAIL}</div>
            </div>
          </button>

          <button
            type="button"
            onClick={openContact}
            data-testid="contact-support-email"
            className="w-full text-left flex items-center gap-4 p-4 rounded-2xl bg-[#F5F8FC] border border-slate-200 hover:border-[#1F5FBF]/40 transition-all"
          >
            <div className="w-11 h-11 rounded-xl bg-[#1F5FBF]/10 text-[#1F5FBF] flex items-center justify-center">
              <Heart className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Contact Support</div>
              <div className="font-semibold text-[#23395D]">{SUPPORT_EMAIL}</div>
            </div>
          </button>
        </div>
      </div>

      {/* CTA Card */}
      <div className="bg-gradient-to-br from-[#1F5FBF] to-[#4CAF50] rounded-3xl p-8 sm:p-10 text-white shadow-xl">
        <Shield className="w-10 h-10 opacity-90" />
        <h3 className="mt-4 text-2xl sm:text-3xl font-extrabold leading-tight">
          See UGatePass in action
        </h3>
        <p className="mt-3 text-white/90 leading-relaxed">
          Send us a quick email and we&apos;ll set up a personalized demo for your
          facility — no commitment required.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={openDemo}
            data-testid="contact-card-request-demo"
            className="inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#1F5FBF] font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
          >
            Request Demo <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={openContact}
            data-testid="contact-card-contact"
            className="inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-full bg-white/10 border border-white/40 text-white font-bold backdrop-blur hover:bg-white/20 transition-all"
          >
            Contact Us
          </button>
        </div>
      </div>
    </div>
  </section>
  );
};

// --- FOOTER ---
const Footer = () => (
  <footer data-testid="footer" className="bg-[#23395D] text-slate-200">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid md:grid-cols-3 gap-10">
      <div>
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="UGatePass" className="h-10 w-10 object-contain bg-white rounded-lg p-1" />
          <span className="font-extrabold text-white text-lg">
            UGate<span className="text-[#8BC34A]">Pass</span>
          </span>
        </div>
        <p className="mt-4 text-sm text-slate-300 leading-relaxed max-w-xs">
          Your Universal Gateway — Smart QR-Based Entry &amp; Exit Management
          for modern organizations.
        </p>
      </div>

      <div>
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-white/70 mb-4">
          Quick Links
        </div>
        <ul className="space-y-2 text-sm">
          <li>
            <a href="#about" className="hover:text-white transition-colors" data-testid="footer-about">
              About UGatePass
            </a>
          </li>
          <li>
            <a href="#contact" className="hover:text-white transition-colors" data-testid="footer-contact">
              Contact Us
            </a>
          </li>
          <li>
            <Link to="/privacy-policy" className="hover:text-white transition-colors" data-testid="footer-privacy">
              Privacy Policy
            </Link>
          </li>
        </ul>
      </div>

      <div>
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-white/70 mb-4">
          Get in Touch
        </div>
        <ul className="space-y-2 text-sm">
          <li className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#8BC34A]" />
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="hover:text-white"
              data-testid="footer-email"
            >
              {SUPPORT_EMAIL}
            </a>
          </li>
          <li className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#8BC34A]" />
            <a
              href="https://www.ugatepass.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white"
              data-testid="footer-website"
            >
              www.ugatepass.com
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-400">
        <div data-testid="footer-copyright">
          UGate Pass © 2026. All Rights Reserved.
        </div>
        <div className="flex items-center gap-5">
          <Link to="/privacy-policy" className="hover:text-white" data-testid="footer-bottom-privacy">
            Privacy Policy
          </Link>
          <a href="#contact" className="hover:text-white" data-testid="footer-bottom-contact">
            Contact Us
          </a>
          <a href="#about" className="hover:text-white" data-testid="footer-bottom-about">
            About
          </a>
        </div>
      </div>
    </div>
  </footer>
);

const LandingPage = () => {
  const [demoOpen, setDemoOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const value = {
    openDemo: () => setDemoOpen(true),
    openContact: () => setContactOpen(true),
  };

  return (
    <EmailDialogContext.Provider value={value}>
      <div className="App bg-white text-[#23395D] antialiased">
        <NavBar />
        <Hero />
        <IdealForFacility />
        <Why />
        <Features />
        <VehiclePass />
        <HowItWorks />
        <Roles />
        <Industries />
        <Commitment />
        <About />
        <CTABand />
        <Contact />
        <Footer />
      </div>
      <EmailActionDialog
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        type="demo"
      />
      <EmailActionDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        type="contact"
      />
    </EmailDialogContext.Provider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
